import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq, citySeo } from "../slugs/cityPricing";

const saintTropez: ChauffeurPage = {
  slug: "saint-tropez",
  title: "VTC & private chauffeur in Saint-Tropez",
  subtitle:
    "Private chauffeur in Saint-Tropez: Pampelonne beaches, the port, villas and the transfer from Nice airport.",
  keywords: [
    "vtc saint-tropez",
    "private chauffeur saint-tropez",
    "pampelonne chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Saint-Tropez",
    subtitle:
      "Pampelonne, the port and the gulf's villas: a chauffeur for the whole season.",
    keywords: [
      "VTC Saint-Tropez",
      "Pampelonne chauffeur",
      "Gulf of Saint-Tropez chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Saint-Tropez",
    badges: ["Pampelonne beaches", "Long-distance transfer", "24/7 service"],
  },
  benefits: {
    heading: "Why a chauffeur in Saint-Tropez",
    items: [
      {
        title: "Pampelonne beaches",
        description:
          "Beach clubs and beach restaurants: drop-off and pick-up with no parking worry in season.",
      },
      {
        title: "Long-distance transfer",
        description:
          "From Nice airport, nearly 2 hours of road handled in high-end comfort.",
      },
      {
        title: "Villa & yachting clientele",
        description:
          "Owners, guests and crews: discretion and flexible timing all summer long.",
      },
      {
        title: "Season mastered",
        description:
          "Summer traffic in the gulf, access to the port and the village anticipated by the chauffeur.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Saint-Tropez",
    services: [
      {
        title: "Nice airport transfer",
        text: "Long-distance Nice ↔ Saint-Tropez link, spacious vehicle and flight tracking.",
      },
      {
        title: "Beach shuttles",
        text: "To the Pampelonne beach clubs (Ramatuelle) and back, with no parking constraint.",
      },
      {
        title: "Hourly hire",
        text: "A dedicated chauffeur by the day for villas, lunches and outings across the gulf.",
      },
      {
        title: "Nights out & port",
        text: "Port-side restaurants, clubs and events, with safe late-night returns.",
      },
      {
        title: "Yachting & villas",
        text: "Crew and guest transfers, coordinated with the villa concierge.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Port of Saint-Tropez",
        description: "Quays, restaurants and yachts at the village's heart.",
      },
      {
        name: "Pampelonne beach",
        description: "Ramatuelle beach clubs, seasonal access.",
      },
      {
        name: "Place des Lices",
        description: "Market, cafés and the village centre.",
      },
      {
        name: "La Ponche",
        description: "Old fishermen's quarter and lanes.",
      },
      {
        name: "Ramatuelle & Gassin",
        description: "Hilltop villages and gulf villas.",
      },
      {
        name: "Port Grimaud",
        description: "Lakeside town at the entrance to the gulf.",
      },
    ],
  },
  generic: {
    heading: "Saint-Tropez: seasonal access and long distances",
    subheading:
      "Nice airport is far, the gulf saturates in summer: a chauffeur who plans for it.",
    paragraphs: [
      "Saint-Tropez has no airport: most arrivals come from Nice Côte d'Azur, nearly two hours away. Over that distance, a spacious vehicle, water, chargers and a rested chauffeur make all the difference; we set the departure time to limit waiting at the entrance to the gulf.",
      "In high season, the coastal road and the approaches to the port saturate. To reach the Pampelonne beach clubs or dine at the port, door-to-door drop-off avoids the search for a space that has become rare and expensive.",
      "Villa and yachting clients expect flexibility and discretion: shifting schedules, beach round-trips, coordination with the concierge. Hourly hire keeps the same chauffeur available throughout the day and the evening.",
    ],
    bullets: ["Spacious vehicle", "Water & chargers", "Flexible timing"],
  },
  process: {
    heading: "Book a chauffeur in Saint-Tropez",
    steps: [
      "Give your Nice flight or your address in the gulf (villa, hotel, port).",
      "We recommend the vehicle based on distance, luggage and group size.",
      "The chauffeur tracks the flight and sets the departure to smooth the arrival.",
      "Direct transfer or hourly hire for beaches and outings during your stay.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Saint-Tropez", "SAINT_TROPEZ"),
    {
      q: "How long does it take from Nice airport to Saint-Tropez?",
      a: "Around 1h30 off-season and up to 2h30 on summer weekends and traffic peaks in the gulf. We adjust the departure time accordingly.",
    },
    {
      q: "Which vehicle for this long transfer with luggage?",
      a: "For the distance and luggage, a premium van (Class V) is the most comfortable. Sedans suit 2 to 3 passengers travelling light.",
    },
    {
      q: "Do you serve the Pampelonne beach clubs?",
      a: "Yes, we run shuttles to the Pampelonne beaches (Ramatuelle) and back, with no parking constraint and flexible timing in summer.",
    },
    {
      q: "Is the service available all summer season?",
      a: "Yes, and booking ahead is strongly advised from June to September: demand is high across the Gulf of Saint-Tropez and availability tightens quickly.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur Nice Airport to Saint-Tropez",
      href: "/en/chauffeur/transfers/nice-airport-saint-tropez",
    },
    {
      label: "Chauffeur Cannes to Saint-Tropez",
      href: "/en/chauffeur/transfers/cannes-saint-tropez",
    },
    {
      label: "Chauffeur Monaco to Saint-Tropez",
      href: "/en/chauffeur/transfers/monaco-saint-tropez",
    },
  ],
  cta: {
    title: "Your private chauffeur in Saint-Tropez",
    text: "Transfer from Nice, a Pampelonne beach or a night at the port: book your chauffeur.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    // MESURE 2026-10-09 : titre sans prix, page en position 36 à 45.
    ...citySeo("en", "Saint-Tropez", "SAINT_TROPEZ", ", car service"),
    canonical: "/en/chauffeur/saint-tropez",
  },
};

export default saintTropez;
